import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, concatMap, lastValueFrom, map, Observable, of, take } from 'rxjs';
import { AuthProject, AuthResponse, Organization, User, UserOrganization } from '../models/user';
import { LoginResponse } from '../models/login-response';
import { Store } from '@ngxs/store';
import { AuthState } from '../store/auth.state';
import { AccountAction, AgentsAvailable, LoginToken, ROLES_AVAILABLE, RolesAvailable } from '../store/auth.actions';
import { MsalService } from '@azure/msal-angular';
import { NavController } from '@ionic/angular';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { ROLES } from '../../constants/enums';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public readonly organizationChanged = new BehaviorSubject<boolean>(false);
  public readonly projectChanged = new BehaviorSubject<boolean>(false);
  private readonly apiAuth: string;

  constructor(private http: HttpClient, private store: Store, private msalService: MsalService) {
    this.apiAuth = `${environment.genaiApiUrl}`;
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiAuth}/user/login`,{user:email,password:password});
  }

  async loginMsal(account: AccountInfo): Promise<Observable<LoginResponse>> {
    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: ['user.read'],
        account: account
      });
      return this.http.post<LoginResponse>(`${this.apiAuth}/user/login`, {
        user: account.username,
        msalToken: 'Bearer ' + result.accessToken
      });
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Session expired - redirect to Azure AD login page
        await this.msalService.instance.acquireTokenRedirect({
          scopes: ['user.read']
        });
        // Browser redirects away, this line won't execute
        return of(null as unknown as LoginResponse);
      }
      throw error;
    }
  }

  authenticate(token: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiAuth}/user/auth`,{token:token});
  }

  getUserClient(): Organization {
    let authResponse = this.store.selectSnapshot(AuthState.getAccount);
    if(authResponse && authResponse.user && authResponse.user.userOrganizations){
      return authResponse.user.userOrganizations[0].organization;
    }
    return {
      id: '',
      name: '',
      description: '',
      path: '',
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      enabled: false
    };
  }

  getUserClients(): UserOrganization[] {
    let authResponse = this.store.selectSnapshot(AuthState.getAccount);
    if(authResponse && authResponse.user && authResponse.user.userOrganizations){
      return authResponse.user.userOrganizations;
    }
    return [];
  }
  getUserProjects() {
    let authResponse = this.store.selectSnapshot(AuthState.getAccount);
    let projects: AuthProject[] = [];
    let projectsIds: string[] = [];
    if(authResponse && authResponse.user && authResponse.user.userRoles){
      authResponse.user.userRoles.forEach(userRole=>{
        if(userRole && userRole.role && userRole.role.project && !projectsIds.includes(userRole.role.project.id)){
          projectsIds.push(userRole.role.project.id);
          projects.push(userRole.role.project);
        }
      });
    }
    return projects;
  }

  getUserProjectsByClient(client: Organization): AuthProject[]{
    let clients = this.getUserClients();
    let userProjects = this.getUserProjects();
    let filteredProjects = userProjects.filter(up => up.genaiOrganizationId === client.id);
    return filteredProjects;
  }

  async msalAuthenticate(account:AccountInfo): Promise<boolean> {
    try {
      let result = await this.loginMsal(account);
      const loginResponse = await lastValueFrom(result);
      await lastValueFrom(this.store.dispatch(new LoginToken(loginResponse.token)));
      const authResponse = await lastValueFrom(this.authenticate(loginResponse.token));
      await lastValueFrom(this.store.dispatch(new AccountAction(authResponse)));
      // Verify the state was updated
      // const storedAccount = this.store.selectSnapshot(AuthState.getAccount);
      // const availableAgents = this.getAvailableAgents();
      // await lastValueFrom(this.store.dispatch(new AgentsAvailable(availableAgents)));
      // const userRoles = this.getUserRoles();
      // await lastValueFrom(this.store.dispatch(new RolesAvailable(userRoles)));
      return true;
    } catch (error) {
      return false;
    }
  }

  updateProjectChanged(changed: boolean): void {
    this.projectChanged.next(changed);
  }

  updateOrganizationChanged(changed: boolean): void {
    this.organizationChanged.next(changed);
  }

  getProjectsByClient(selectedClientId: string): Observable<AuthProject[]> {
    return this.http.get<AuthProject[]>(`${this.apiAuth}/projects/client/${selectedClientId}`);
  }

  getIsAdmin(): boolean {
    const account = this.store.selectSnapshot(AuthState.getAccount);
    let isAdmin = false;
    if(account.user.userOrganizations){
      let org = account.user.userOrganizations.find(org=>org.admin);
      if(org){
        isAdmin = true;
      }
    }
    return isAdmin;
  }

  getIsClientAdmin(clientId: string):boolean {
    const account = this.store.selectSnapshot(AuthState.getAccount);
    let isAdmin = false;
    if(account.user.userOrganizations){
      let org = account.user.userOrganizations.find(org=>org.admin && org.organization.id === clientId);
      if(org){
        isAdmin = true;
      }
    }
    return isAdmin;
  }

  async getIsProjectAdmin(projectId: string): Promise<boolean> {
    const account = this.store.selectSnapshot(AuthState.getAccount);
    let isAdmin = false;
    let orgsAdmin: string[] = [];
    if(account.user.userOrganizations){
      let userOrgAdmin = account.user.userOrganizations.filter(org=>org.admin);
      userOrgAdmin.map(uo=>orgsAdmin.push(uo.organization.id));
      if(account.user.userRoles){
        let userRole = account.user.userRoles.find(ur=>ur.role.project.id === projectId);
        if(userRole){
          isAdmin = orgsAdmin.includes(userRole.role.project.genaiOrganizationId);
        }
      }
      let project = await lastValueFrom(this.getProjectByProjectId(projectId));
      if(orgsAdmin.includes(project.genaiOrganizationId)){
        isAdmin = true;
      }
    }
    return isAdmin;
  }

  getIsSuperadmin(): boolean {
    const account = this.store.selectSnapshot(AuthState.getAccount);
    return account.user.superadmin;
  }

  getIsManager(projectId: string): boolean {
    let isManager = false;
    const account = this.store.selectSnapshot(AuthState.getAccount);
    if(account.user.userRoles){
      let userRole = account.user.userRoles.find(ur=>ur.role.project.id === projectId);
      if(userRole?.role.name === ROLES.MANAGER){
        isManager = true;
      }
    }
    return isManager;
  }

  getIsUser(projectId: string): boolean {
    let isUser = false;
    const account = this.store.selectSnapshot(AuthState.getAccount);
    if(account.user.userRoles){
      let userRole = account.user.userRoles.find(ur=>ur.role.project.id === projectId);
      if(userRole?.role.name === ROLES.USER){
        isUser = true;
      }
    }
    return isUser;
  }

  getProjectByProjectId(projectId: string): Observable<AuthProject> {
    return this.http.get<AuthProject>(`${this.apiAuth}/admin/project/${projectId}`);
  }

  // Lo uso para llamadas a SharePoint: pido el token de Microsoft (MSAL) y lo devuelvo
  // en formato "Bearer xxx" para meterlo en el header msal-token que pide el back
  async getMsalAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      throw new Error('No hay cuenta MSAL activa');
    }
    const result = await this.msalService.instance.acquireTokenSilent({
      scopes: ['user.read'],
      account
    });
    return 'Bearer ' + result.accessToken;
  }

  getClientByClientId(clientId: string): Observable<Organization> {
    return this.http.get<Organization>(`${this.apiAuth}/admin/organization/${clientId}`);
  }
}
